import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useComplianceAlerts } from "@/hooks/use-realtime-updates";
import { useExecutiveStrategicAlerts } from "@/hooks/use-executive-demo-data";

interface Alert {
  id: string;
  type: "urgent" | "opportunity" | "anomaly";
  icon: string;
  iconColor: string;
  bgColor: string;
  title: string;
  message: string;
  timestamp: string;
  isNew?: boolean;
}

interface ComplianceAlert {
  type: string;
  severity: 'urgent' | 'high' | 'medium' | 'low';
  message: string;
}

interface StrategicAlertsProps {
  demoMode?: boolean;
}

export const StrategicAlerts: React.FC<StrategicAlertsProps> = ({ demoMode = true }) => {
  const { data: demoData, isLoading: demoLoading } = useExecutiveStrategicAlerts();
  const { latestUpdate, isConnected, updates } = useComplianceAlerts();
  const [alerts, setAlerts] = useState<Alert[]>([]);

  // Initialize with demo data in demo mode
  useEffect(() => {
    if (demoMode && demoData) {
      const demoAlerts: Alert[] = demoData.map((alert, index) => ({
        id: `demo-${index}`,
        type: alert.type === 'opportunity' ? 'opportunity' as const :
              alert.type === 'warning' ? 'urgent' as const : 'anomaly' as const,
        icon: alert.type === 'opportunity' ? 'fa-lightbulb' :
              alert.type === 'warning' ? 'fa-calendar-times' : 'fa-chart-simple',
        iconColor: alert.type === 'opportunity' ? 'text-success' :
                   alert.type === 'warning' ? 'text-warning' : 'text-info',
        bgColor: alert.type === 'opportunity' ? 'bg-green-100' :
                 alert.type === 'warning' ? 'bg-yellow-100' : 'bg-blue-100',
        title: alert.title,
        message: alert.description,
        timestamp: alert.timestamp,
        isNew: false,
      }));
      setAlerts(demoAlerts);
      return;
    }
  }, [demoMode, demoData]);

  // Convert compliance alert to display alert
  const convertComplianceAlert = (complianceAlert: ComplianceAlert): Alert => {
    const alertTypeMap = {
      vat_deadline: { type: "urgent" as const, icon: "fa-calendar-times", iconColor: "text-danger", bgColor: "bg-red-100" },
      audit_required: { type: "urgent" as const, icon: "fa-shield-halved", iconColor: "text-danger", bgColor: "bg-red-100" },
      missing_documents: { type: "anomaly" as const, icon: "fa-file-exclamation", iconColor: "text-warning", bgColor: "bg-yellow-100" },
      anomaly_detected: { type: "anomaly" as const, icon: "fa-chart-simple", iconColor: "text-warning", bgColor: "bg-yellow-100" },
    };

    const alertConfig = alertTypeMap[complianceAlert.type as keyof typeof alertTypeMap] ||
                       { type: "anomaly" as const, icon: "fa-bell", iconColor: "text-warning", bgColor: "bg-yellow-100" };

    return {
      id: `alert_${Date.now()}_${Math.random()}`,
      ...alertConfig,
      title: complianceAlert.severity === 'urgent' ? 'Urgent:' :
             complianceAlert.severity === 'high' ? 'High Priority:' : 'Alert:',
      message: complianceAlert.message,
      timestamp: new Date().toLocaleString(),
      isNew: true
    };
  };

  // Handle new compliance alerts
  useEffect(() => {
    if (latestUpdate?.type === 'compliance_alert') {
      const newAlert = convertComplianceAlert(latestUpdate.data);
      setAlerts(prev => [newAlert, ...prev.slice(0, 9)]); // Keep last 10 alerts

      // Remove "new" flag after 5 seconds
      setTimeout(() => {
        setAlerts(prev => prev.map(alert =>
          alert.id === newAlert.id ? { ...alert, isNew: false } : alert
        ));
      }, 5000);
    }
  }, [latestUpdate]);

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-brand-primary">Strategic Alerts</h3>
          <Badge variant={isConnected ? "default" : "secondary"} className="text-xs">
            <i className={`fa-solid ${isConnected ? 'fa-wifi' : 'fa-wifi-slash'} mr-1`}></i>
            {isConnected ? 'Live' : 'Offline'}
          </Badge>
          {alerts.some(alert => alert.isNew) && (
            <Badge variant="destructive" className="text-xs animate-pulse">
              {alerts.filter(alert => alert.isNew).length} New
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="sm" className="p-1.5 h-auto">
          <i className="fa-solid fa-ellipsis text-muted-foreground" />
        </Button>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <i className="fa-solid fa-check-circle text-2xl mb-2 block text-green-500"></i>
            <p className="text-sm">All clear! No active alerts.</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.id} className={`flex items-start gap-3.5 transition-all duration-300 ${alert.isNew ? 'bg-blue-50 border border-blue-200 rounded-lg p-3 -m-1' : ''}`}>
              <div className={`w-8 h-8 mt-1 flex-shrink-0 ${alert.bgColor} rounded-full flex items-center justify-center`}>
                <i className={`fa-solid ${alert.icon} ${alert.iconColor}`} />
              </div>
              <div className="flex-1">
                <p className="text-xs text-foreground">
                  <span className="font-bold">{alert.title}</span> {alert.message}
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">{alert.timestamp}</p>
              </div>
              {alert.isNew && (
                <button
                  onClick={() => dismissAlert(alert.id)}
                  className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <i className="fa-solid fa-times text-xs"></i>
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};